/**
 * The others at the table, handling their cards.
 *
 * When another player picks a card up out of their hand, or puts a finger on
 * one in the trick, this draws it on your table — and, more than the card,
 * draws *them*: whose hand is on it has to be felt before it is read.
 *
 *   - Their name plate warms, lit from behind in brass, and breathes.
 *   - A thread of light runs from their seat, curving across the felt, to their
 *     fingertip, with light travelling along it towards the card: the line of
 *     their reach, which is what you actually see at a real table.
 *   - Their fingertip is a soft glow on the card, which ripples outward once as
 *     they first touch it.
 *   - Their name rides along beside it, small, in case the thread is not enough.
 *   - A card from their hand is a back rising out of their own fan and growing
 *     as it comes towards the middle, rimmed in the same light. It never says
 *     which card, or where in the hand it was (shared/protocol/gesture.ts).
 *
 * Throw it and the card they play flies from exactly where the ghost was let
 * go (flight.ts `noteRemoteOrigin`), so you see it leave their hand and land.
 *
 * Positions arrive ~14 times a second and are eased towards every frame, so a
 * card moves smoothly between messages. Everything that moves is written
 * straight to the DOM from one animation frame loop; React only mounts and
 * unmounts a gesture's elements, which happens when a card is picked up or let
 * go and never while it is moving.
 */
import { useEffect, useRef, useState } from 'react'
import type { GestureRelay, GestureTarget, Seat } from '@cards/shared'
import { cardSrc } from '../cards/assets'
import { prefersReducedMotion, useSettings } from '../settings/settings'
import { noteRemoteOrigin } from './flight'
import { subscribeGestures } from './gestures'
import { alongFrom, fromTable, measureFrame, slotOffset } from './tableFrame'
import type { Position, TableView } from './view'

/** Per-frame easing towards the latest message. */
const FOLLOW = 0.3
/** A gesture nobody has said anything about for this long has lost its
 *  sender (a dropped connection mid-drag) and is let go. */
const STALE_MS = 2500
/** A throw that was not followed by the play (it was refused) is let go. */
const THROWN_MS = 1600
/** How long the letting go takes to fade before the elements are removed. */
const LEAVE_MS = 420

/** The angle a card is held at in that seat's fan of backs (ui.css). */
const FAN_ANGLE: Record<Position, number> = { s: 0, w: 90, n: 0, e: -90 }

interface Held {
  readonly seat: Seat
  readonly position: Position
  readonly target: GestureTarget
  /** In the sender's table coordinates (tableFrame.ts). */
  goal: { x: number; y: number; lean: number }
  cur: { x: number; y: number; lean: number }
  phase: 'held' | 'thrown' | 'leaving'
  at: number
}

interface Mounted {
  readonly seat: Seat
  readonly position: Position
  readonly target: GestureTarget
  readonly name: string
  readonly phase: Held['phase']
}

export function RemoteHands({ view }: { view: TableView }): JSX.Element | null {
  const [settings] = useSettings()
  const layer = useRef<HTMLDivElement | null>(null)
  const held = useRef(new Map<Seat, Held>())
  const [mounted, setMounted] = useState<readonly Mounted[]>([])
  const viewRef = useRef(view)
  viewRef.current = view
  const enabled = settings.seeMoves && !prefersReducedMotion()

  useEffect(() => {
    if (!enabled) return
    let frame = 0
    const remount = () => {
      const v = viewRef.current
      setMounted(
        [...held.current.values()].map((h) => ({
          seat: h.seat,
          position: h.position,
          target: h.target,
          name: v.name(h.seat),
          phase: h.phase,
        })),
      )
    }
    const leave = (h: Held) => {
      if (h.phase === 'leaving') return
      h.phase = 'leaving'
      // Back down into the hand it came out of.
      h.goal = h.target.kind === 'hand' ? { x: 0, y: 2, lean: 0 } : { ...h.cur }
      h.at = performance.now()
      if (h.target.kind === 'trick') releaseTrickCard(h.target.seat)
      remount()
      window.setTimeout(() => {
        if (held.current.get(h.seat) === h) {
          held.current.delete(h.seat)
          remount()
        }
        // Back in the fan: the gap it left closes as the ghost fades into it.
        if (h.target.kind === 'hand') setLifting(h.position, false)
      }, LEAVE_MS)
    }

    const onGesture = (g: GestureRelay) => {
      const v = viewRef.current
      if (g.seat === v.mySeat) return
      const position = v.seats[g.seat]?.position
      if (!position || position === 's') return
      const now = performance.now()
      let h = held.current.get(g.seat)
      const sameTarget = h && h.phase !== 'leaving' && sameGestureTarget(h.target, g.target)
      if (!h || !sameTarget) {
        if (g.phase !== 'move') return
        h = {
          seat: g.seat,
          position,
          target: g.target,
          goal: { x: g.x, y: g.y, lean: g.lean },
          // A card from a hand starts in the middle of that hand; one on the
          // table starts where it is.
          cur: g.target.kind === 'hand' ? { x: 0, y: 2, lean: 0 } : { x: g.x, y: g.y, lean: g.lean },
          phase: 'held',
          at: now,
        }
        held.current.set(g.seat, h)
        remount()
      }
      h.at = now
      if (g.phase === 'end') return leave(h)
      h.goal = { x: g.x, y: g.y, lean: g.lean }
      if (g.phase === 'throw') {
        h.phase = 'thrown'
        // The flight starts where the ghost is *seen*, not at the let-go: a
        // flick outruns the ~14 messages a second, and jumping the card ahead
        // to where the finger ended up reads as a glitch, not a throw.
        h.goal = { ...h.cur }
        place(layer.current, h)
        const ghost = layer.current?.querySelector<HTMLElement>(`[data-ghost="${h.seat}"]`)
        if (ghost) noteRemoteOrigin(h.position, ghost, FAN_ANGLE[h.position] + h.cur.lean)
        remount()
      }
      if (!frame) frame = requestAnimationFrame(tick)
    }

    const tick = () => {
      frame = 0
      const now = performance.now()
      let busy = false
      for (const h of held.current.values()) {
        if (h.phase === 'held' && now - h.at > STALE_MS) leave(h)
        if (h.phase === 'thrown' && now - h.at > THROWN_MS) leave(h)
        h.cur = {
          x: h.cur.x + (h.goal.x - h.cur.x) * FOLLOW,
          y: h.cur.y + (h.goal.y - h.cur.y) * FOLLOW,
          lean: h.cur.lean + (h.goal.lean - h.cur.lean) * FOLLOW,
        }
        place(layer.current, h)
        busy = true
      }
      if (busy) frame = requestAnimationFrame(tick)
    }

    const off = subscribeGestures(onGesture)
    return () => {
      off()
      if (frame) cancelAnimationFrame(frame)
      for (const h of held.current.values()) {
        if (h.target.kind === 'trick') releaseTrickCard(h.target.seat)
        else setLifting(h.position, false)
      }
      held.current.clear()
      setMounted([])
    }
  }, [enabled])

  // A thrown card that has now been played is on the table in its own right:
  // the ghost goes in the same frame the real card starts its flight.
  const played = new Set((view.trick?.plays ?? []).map((p) => p.seat))
  useEffect(() => {
    let changed = false
    for (const h of held.current.values()) {
      if (h.phase === 'thrown' && played.has(h.seat)) {
        held.current.delete(h.seat)
        // The fan is a card shorter now in its own right.
        setLifting(h.position, false)
        changed = true
      }
    }
    if (changed) setMounted((m) => m.filter((x) => !(x.phase === 'thrown' && played.has(x.seat))))
  })

  if (!enabled) return null
  const visible = mounted.filter((m) => !(m.phase === 'thrown' && played.has(m.seat)))

  return (
    <div className="remote-layer" ref={layer} aria-hidden="true">
      <svg className="remote-threads">
        <defs>
          <filter id="thread-blur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.4" />
          </filter>
          {visible.map((m) => (
            <linearGradient key={m.seat} id={`thread-${m.seat}`} gradientUnits="userSpaceOnUse" data-thread-grad={m.seat}>
              <stop offset="0" stopColor="var(--accent)" stopOpacity="0" />
              <stop offset="0.55" stopColor="var(--accent)" stopOpacity="0.45" />
              <stop offset="1" stopColor="var(--accent-text)" stopOpacity="0.95" />
            </linearGradient>
          ))}
        </defs>
        {visible.map((m) => (
          <g key={m.seat} className={`remote-thread ${m.phase}`} data-thread={m.seat}>
            <path className="remote-thread-glow" stroke={`url(#thread-${m.seat})`} filter="url(#thread-blur)" />
            <path className="remote-thread-line" stroke={`url(#thread-${m.seat})`} />
            <path className="remote-thread-spark" />
          </g>
        ))}
      </svg>
      {visible.map((m) => (
        <div key={m.seat} className={`remote-hold ${m.phase}`} data-hold={m.seat}>
          <div className="remote-plate" data-plate={m.seat} />
          {m.target.kind === 'hand' ? (
            <div className="remote-ghost" data-ghost={m.seat}>
              <img src={cardSrc('BACK', settings.deck)} alt="" />
            </div>
          ) : null}
          <div className="remote-touch" data-touch={m.seat}>
            <i />
          </div>
          <div className="remote-tag" data-tag={m.seat}>
            <span>{m.name}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

function sameGestureTarget(a: GestureTarget, b: GestureTarget): boolean {
  return a.kind === b.kind && (a.kind === 'hand' || (b.kind === 'trick' && a.seat === b.seat))
}

/** A card is out of this seat's fan: one back goes from it while the ghost is
 *  in the air, so the hand is seen to be a card lighter. An attribute React
 *  does not manage, so no re-render of the seat can put the card back. */
function setLifting(position: Position, on: boolean): void {
  const fan = document.querySelector<HTMLElement>(`.play-felt .seat-${position} .seat-fan`)
  if (!fan) return
  if (on) fan.dataset.lifting = ''
  else delete fan.dataset.lifting
}

function trickCard(seat: Seat): HTMLElement | null {
  return document.querySelector<HTMLElement>(`.play-felt .tcard[data-seat="${seat}"]`)
}

/** Hand a trick card back to the table: clearing the nudge lets its own
 *  transition carry it home (app.css). */
function releaseTrickCard(seat: Seat): void {
  const el = trickCard(seat)
  if (!el) return
  el.style.translate = ''
  el.style.rotate = ''
  delete el.dataset.held
}

/** Put one gesture where it is this frame: the card, the fingertip, the name,
 *  the thread from the seat, and the light behind the seat's plate. */
function place(layer: HTMLDivElement | null, h: Held): void {
  if (!layer) return
  const f = measureFrame()
  const ring = layer.getBoundingClientRect()
  if (!f || !ring.width) return
  const seatEl = layer.parentElement?.querySelector<HTMLElement>(`.seat-${h.position}`)
  const plateEl = seatEl?.querySelector<HTMLElement>('.seat-label') ?? seatEl
  if (!seatEl || !plateEl) return
  const plate = centre(plateEl.getBoundingClientRect(), ring)

  let grip: [number, number]
  let cardH = 0
  if (h.target.kind === 'hand') {
    const ghost = layer.querySelector<HTMLElement>(`[data-ghost="${h.seat}"]`)
    const fan = seatEl.querySelector<HTMLElement>('.seat-fan-cards')
    if (!ghost) return
    if (h.phase !== 'leaving') setLifting(h.position, true)
    // Their hand is `y` = 2: on this screen, that seat's fan of backs.
    const fr = (fan ?? seatEl).getBoundingClientRect()
    const hand = alongFrom(h.position, f, fr.left + fr.width / 2, fr.top + fr.height / 2)
    const [px, py] = fromTable(h.cur.x, h.cur.y, h.position, f, hand)
    const back = fan?.querySelector('img')
    const small = (back?.offsetWidth ?? 20) / (ghost.offsetWidth || 1)
    // Out of the fan at the fan's own size, growing to a card on the table by
    // the time it is over their own slot.
    const out = Math.min(1, Math.max(0, 2 - h.cur.y))
    const scale = small + (1 - small) * ease(out)
    grip = [px - ring.left, py - ring.top]
    cardH = ghost.offsetHeight * scale
    const angle = FAN_ANGLE[h.position] + h.cur.lean
    ghost.style.transform =
      `translate(${(grip[0] - ghost.offsetWidth / 2).toFixed(1)}px, ${(grip[1] - ghost.offsetHeight / 2).toFixed(1)}px) ` +
      `rotate(${angle.toFixed(2)}deg) scale(${scale.toFixed(3)})`
  } else {
    const el = trickCard(h.target.seat)
    if (!el) return
    if (h.phase !== 'leaving') {
      // Where it is aimed, less where it lies: the card's own slot on this
      // screen is its resting place, whatever that slot is called here.
      const lies = (['n', 'e', 's', 'w'] as const).find((p) => el.classList.contains(p)) ?? 's'
      const [rx, ry] = slotOffset(lies, f)
      const [px, py] = fromTable(h.cur.x, h.cur.y, h.position, f, (h.position === 's' || h.position === 'n' ? f.sy : f.sx) * 2)
      el.dataset.held = ''
      el.style.translate = `${(px - f.cx - rx).toFixed(1)}px ${(py - f.cy - ry).toFixed(1)}px`
      el.style.rotate = `${h.cur.lean.toFixed(2)}deg`
    }
    grip = centre(el.getBoundingClientRect(), ring)
    cardH = el.offsetHeight
  }

  const at = (sel: string) => layer.querySelector<HTMLElement>(sel)
  const touch = at(`[data-touch="${h.seat}"]`)
  if (touch) touch.style.transform = `translate(${grip[0].toFixed(1)}px, ${grip[1].toFixed(1)}px)`
  const tag = at(`[data-tag="${h.seat}"]`)
  if (tag) tag.style.transform = `translate(${grip[0].toFixed(1)}px, ${(grip[1] - cardH / 2 - 8).toFixed(1)}px)`
  const plateGlow = at(`[data-plate="${h.seat}"]`)
  if (plateGlow) plateGlow.style.transform = `translate(${plate[0].toFixed(1)}px, ${plate[1].toFixed(1)}px)`

  // The thread bows towards the middle of the table, the way an arm reaching
  // across it does, rather than cutting straight along the edge.
  const mid: [number, number] = [(plate[0] + grip[0]) / 2, (plate[1] + grip[1]) / 2]
  const centreOfTable: [number, number] = [ring.width / 2, ring.height * 0.52]
  const ctrl: [number, number] = [mid[0] + (centreOfTable[0] - mid[0]) * 0.35, mid[1] + (centreOfTable[1] - mid[1]) * 0.35]
  const d = `M${plate[0].toFixed(1)} ${plate[1].toFixed(1)} Q${ctrl[0].toFixed(1)} ${ctrl[1].toFixed(1)} ${grip[0].toFixed(1)} ${grip[1].toFixed(1)}`
  const g = layer.querySelector(`[data-thread="${h.seat}"]`)
  g?.querySelectorAll('path').forEach((p) => p.setAttribute('d', d))
  const grad = layer.querySelector(`[data-thread-grad="${h.seat}"]`)
  if (grad) {
    grad.setAttribute('x1', plate[0].toFixed(1))
    grad.setAttribute('y1', plate[1].toFixed(1))
    grad.setAttribute('x2', grip[0].toFixed(1))
    grad.setAttribute('y2', grip[1].toFixed(1))
  }
}

function centre(r: DOMRect, ring: DOMRect): [number, number] {
  return [r.left + r.width / 2 - ring.left, r.top + r.height / 2 - ring.top]
}

function ease(t: number): number {
  return 1 - (1 - t) * (1 - t)
}
